import clsx from "clsx";
import { Trans } from "react-i18next";
import { Btn } from "@/ui/btn/Btn";
import { insertBetween } from "@/utils/array";
import { getCurrencyParts } from "@/utils/number";
import { useCalculator } from "../../hooks/useCalculator";
import { useCalculatorUpdate } from "../../hooks/useCalculatorUpdate";
import type { Calc } from "../../types";
import { CalculatorMessage } from "../calculator-message/CalculatorMessage";
import styles from "./CalculatorAsideContent.module.css";

export const CalculatorAsideContent = () => {
  const { data, isValidating } = useCalculatorUpdate();

  return (
    <div className={clsx(styles.content, isValidating && styles.loading)}>
      {data.content.map((part, index) => (
        <AsidePart key={index} part={part} />
      ))}
    </div>
  );
};

const AsidePart: React.FC<{
  part: Calc.Content.Part;
}> = ({ part }) => {
  switch (part.type) {
    case "title":
      return <div className={styles.title}>{part.item}</div>;
    case "price":
      return <AsidePrice price={part.item} />;
    case "info":
      return <AsideInfo {...part} />;
  }
};

interface AsidePriceProps {
  price: Calc.Price.Price;
}

const AsidePrice: React.FC<AsidePriceProps> = ({ price }) => {
  const parts = getCurrencyParts(price.value, price.currency);
  const labelParts =
    price.label !== undefined
      ? insertBetween(price.label, () => {
          return {
            type: "text",
            text: " ",
          };
        })
      : undefined;

  return (
    <div
      className={clsx(styles.price, [
        price.discount !== undefined && styles.discount,
      ])}
    >
      <div className={styles.currency}>{parts.currency}</div>
      <div className={styles.value}>
        {parts.plusSign}
        {parts.minusSign}
        <div className={styles.integer}>{parts.integer}</div>
        <div className={styles.decimal}>{parts.decimal}</div>
        <div className={styles.fraction}>{parts.fraction}</div>
      </div>
      {labelParts !== undefined && (
        <div className={styles.label}>
          {labelParts.map((part, index) => (
            <span key={index} className={styles[part.type]}>
              {part.text}
            </span>
          ))}
        </div>
      )}
      {price.period !== undefined && (
        <div className={styles.period}>{price.period}</div>
      )}
    </div>
  );
};

const AsideInfo: React.FC<Calc.Content.InfoPart> = ({ item }) => {
  return (
    <div className={styles.info}>
      {item.map((part, index) => (
        <AsideInfoPart key={index} part={part} />
      ))}
    </div>
  );
};

const AsideInfoPart: React.FC<{
  part:
    | Calc.Content.MessagePart
    | Calc.Content.TrialPart
    | Calc.Content.PromoCodePart;
}> = ({ part }) => {
  switch (part.type) {
    case "message":
      return <CalculatorMessage {...part} />;
    case "trial":
      return <AsideTrial {...part} />;
    case "promo-code":
      return <AsidePromoCode {...part} />;
  }
};

const AsideTrial: React.FC<Calc.Content.TrialPart> = () => {
  const isTrialActive = useCalculator((state) => state.isTrialActive);
  const setTrialActive = useCalculator((state) => state.setTrialActive);

  if (isTrialActive === true) {
    return (
      <div className={styles.link} onClick={() => setTrialActive(false)}>
        <Trans>I don't need trial</Trans>
      </div>
    );
  }

  return (
    <div className={styles.link} onClick={() => setTrialActive(true)}>
      <Trans>Go to trial</Trans>
    </div>
  );
};

const AsidePromoCode: React.FC<Calc.Content.PromoCodePart> = () => {
  const promoCode = useCalculator((state) => state.promoCode);
  const setPromoCode = useCalculator((state) => state.setPromoCode);
  const setPromoCodeDialogOpen = useCalculator(
    (state) => state.setPromoCodeDialogOpen,
  );

  if (promoCode !== undefined) {
    return (
      <div className={styles.code}>
        <div className={styles.label}>
          <Trans>Applied promo code:</Trans>
        </div>
        <div className={styles.value}>
          <div className={styles.text}>{promoCode}</div>
          <Btn
            icon="cross_16px"
            size="2xs"
            mode="remove"
            onClick={() => setPromoCode(undefined)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.link} onClick={() => setPromoCodeDialogOpen(true)}>
      <Trans>I have a Promo Code</Trans>
    </div>
  );
};
